import { useState, useMemo } from 'react';

import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';

import { PrimaryButton, SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { TreeView, TreeViewQueryResponse } from '../../folders/treeView';

const displayText = {
  cancel: 'Cancel',
  modalHeader: 'Move',
  moveToRoot: 'Move to overview',
  newFolder: 'New folder',
  next: 'Move to folder',
};

type MoveToFolderModalProps = {
  currentFolderId: FolderId;
  rootFolderId: FolderId;
  /** Lowercase, singular name for non-folder content items being moved (i.e. 'campaign', 'workflow') */
  contentItemName: string;
  onClose: () => void;
  onConfirm: (
    folderIds: FolderId[],
    contentIds: number[],
    destination: AncestorIdData & AncestorNameData,
  ) => void;
  treeViewQuery: (id: FolderId) => TreeViewQueryResponse;
  folderIds: FolderId[];
  contentIds: number[];
};

const getHeaderText = (folderCount: number, contentCount: number, contentItemName: string) => {
  const folderText = `folder${folderCount > 1 ? 's' : ''}`;
  const contentText = `${contentItemName}${contentCount > 1 ? 's' : ''}`;
  if (folderCount && contentCount) {
    return `Move ${folderText} and ${contentText} `;
  } else if (folderCount) {
    return `Move ${folderText}`;
  } else {
    return `Move ${contentText}`;
  }
};

export const MoveToFolderModal = ({
  currentFolderId,
  onClose,
  onConfirm,
  rootFolderId,
  treeViewQuery,
  folderIds,
  contentIds,
  contentItemName,
  ...props
}: MoveToFolderModalProps) => {
  const { folderData: currentFolder, isLoading } = treeViewQuery(currentFolderId);
  const pathInfo = useMemo(
    () => [...currentFolder.ancestors, { id: currentFolder.id, name: currentFolder.name }],
    [currentFolder],
  );
  const [selectedFolder, setSelectedFolder] = useState({
    id: currentFolder.id,
    name: currentFolder.name,
  });
  const handleMoveToSelectedFolder = () => onConfirm(folderIds, contentIds, selectedFolder);
  const handleMoveToRootFolder = () => onConfirm(folderIds, contentIds, currentFolder.ancestors[0]);

  return (
    <Modal
      data-test="move-content-modal-wrapper"
      id="move-content-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
      {...props}
    >
      <ModalHeader data-test="move-content-modal-header">
        {getHeaderText(folderIds.length, contentIds.length, contentItemName)}
      </ModalHeader>
      <ModalBody data-test="move-content-modal-body" id="move-content-modal-body">
        <TreeView
          isLoading={isLoading}
          defaultSelectedAncestors={pathInfo}
          defaultSelectedFolder={currentFolderId}
          defaultSelectedFolderName={currentFolder.name}
          folderIdsToMove={folderIds}
          handleChange={setSelectedFolder}
          query={treeViewQuery}
          searchText={''}
          rootFolderId={rootFolderId}
        />
      </ModalBody>
      <ModalFooter
        cancelLabel={displayText.cancel}
        data-test="move-content-modal-actions"
        id="move-content-modal-actions"
      >
        <SecondaryButton
          data-test="move-to-root-button"
          isDisabled={currentFolderId === rootFolderId}
          onClick={handleMoveToRootFolder}
          size={BUTTON_SIZES.LARGE}
        >
          {displayText.moveToRoot}
        </SecondaryButton>
        <PrimaryButton
          data-test="next-button"
          onClick={handleMoveToSelectedFolder}
          isDisabled={selectedFolder.id === currentFolderId}
          size={BUTTON_SIZES.LARGE}
        >
          {displayText.next}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
